/**
 * Utilidades de cálculo para los reportes de inventario.
 *
 * Los reportes (p. ej. DailyInventoryDistribution) pueden venir consolidados
 * (un registro por fecha con todos los SKUs sumados) o agrupados por proveedor
 * (`groupByNit = true`), en cuyo caso llega un registro por cada NIT y fecha.
 *
 * Cuando el usuario selecciona uno o varios NITs, los componentes deben filtrar
 * por TODOS los NITs seleccionados y luego agregar (sumar) sus conteos por fecha
 * para reflejar el inventario consolidado de esa selección.
 */

/**
 * Filtra los registros por la lista de NITs seleccionada.
 *
 * - Si `nitList` está vacío o solo contiene valores reservados (como `'1'`, que
 *   representa la vista consolidada/todos), se devuelven TODOS los registros sin
 *   filtrar. Esto cubre tanto la vista consolidada como la agrupada sin selección.
 * - En caso contrario se devuelven únicamente los registros cuyo `Nit` esté en la
 *   lista, permitiendo que varios proveedores se agreguen luego por fecha.
 *
 * @param {Array} data - Registros de inventario (DailyInventoryDistributionRecord).
 * @param {string[]} [nitList] - Lista de NITs seleccionados.
 * @returns {Array} Registros filtrados (el mismo array `data` si no hay filtro).
 */
export function filterByNits(data, nitList) {
  if (!Array.isArray(data) || data.length === 0) return [];

  const validNits = Array.isArray(nitList)
    ? nitList.filter((n) => n && n !== '1')
    : [];

  if (validNits.length === 0) return data;

  return data.filter((d) => validNits.includes(d.Nit));
}

/**
 * Agrega los registros sumando sus conteos por `RecordDate`.
 *
 * Útil para consolidar varios NITs en un solo valor por día. Sume los campos
 * numéricos que necesite cada componente; aquí se incluyen todos los rangos de
 * color más el total para que reutilice la misma agregación.
 *
 * @param {Array} data - Registros ya filtrados (p. ej. con {@link filterByNits}).
 * @returns {Object[]} Registros agregados por fecha, ordenados ascendentemente.
 */
export function aggregateByDate(data) {
  if (!Array.isArray(data) || data.length === 0) return [];

  const groupedByDate = {};
  data.forEach((record) => {
    const date = record.RecordDate;
    if (!groupedByDate[date]) {
      groupedByDate[date] = {
        RecordDate: date,
        NegroCount: 0,
        RojoCount: 0,
        AmarilloCount: 0,
        VerdeCount: 0,
        AzulCount: 0,
        TotalReferences: 0,
      };
    }
    groupedByDate[date].NegroCount += record.NegroCount || 0;
    groupedByDate[date].RojoCount += record.RojoCount || 0;
    groupedByDate[date].AmarilloCount += record.AmarilloCount || 0;
    groupedByDate[date].VerdeCount += record.VerdeCount || 0;
    groupedByDate[date].AzulCount += record.AzulCount || 0;
    groupedByDate[date].TotalReferences += record.TotalReferences || 0;
  });

  return Object.values(groupedByDate).sort((a, b) =>
    a.RecordDate.localeCompare(b.RecordDate)
  );
}

export default { filterByNits, aggregateByDate };
